import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ATHLETE_LANDING } from "../../../../constants";
import { fetchUserByEmail, logout } from "../../../../store/actions/userActions";
import { fetchEntitlement } from "../../../../store/actions/entitlementActions";
import AuthFrame from "../../../auth/v2/AuthFrame";
import { TitleBlock, Actions, PrimaryBtn } from "../../../auth/v2/pieces";
import { Loading } from "../../../ForBrands/ui/BrandUI";
import { useIsPhone } from "../../../ForBrands/useIsPhone";
import { useWizardSession } from "../../../auth/v2/useWizardSession";
import { socialPending, watchSocialScore } from "../socialScoreWatch";
import { getOnboarding, patchOnboarding, saveProgress, waitFor, uploadAvatar, compressImage, createCheckout, redeemPromo, recomputeSelf, bindSession, sessionChanged, fetchEarlierProfiles, claimEarlierProfile, fetchStateCodes, SCORE_REFRESH_TIMEOUT_MS } from "./onboardingV2Services";
import { HeroBand, initialsOf, isSocialLink, socialCount, plural, visibilityRead } from "./onboardingPieces";
import { SocialLinksStep, SocialImportingStep, SocialFailedStep, SocialPartialStep, SocialCapturedStep } from "./steps/SocialSteps";
import { TeamCodeStep, TeamDetailsStep, TeamSearchStep, IdentityStep, TeamManualStep, EarlierProfileStep } from "./steps/TeamSteps";
import { PerformanceChoiceStep, SeasonStatsStep, PerformanceDoneStep, NoDataStep } from "./steps/PerformanceSteps";
import { PhotoStep } from "./steps/PhotoStep";
import { PlanStep, PromoStep, PromoAppliedStep, promoHeroSub } from "./steps/PlanSteps";
import { ProfileLiveStep, OptimizedStep, DetailsStep, PrivacyStep, ResumeStep, isPremium, liveHeroSub } from "./steps/LiveSteps";
import { coverageOf } from "../../teamCoverage";
import { fillNoteOf } from "../../../auth/Onboarding/codeTeams";

// Athlete onboarding V2 (Figma "Athlete Onboarding"). One state object from
// /athlete/onboarding/ drives every screen; each step writes only its own
// fields and records progress so the wizard resumes where the athlete left
// off. Team verification reuses the coach-code flow; team search, roster
// identity and completion reuse the existing onboarding endpoints.
//
// Notes of 10/19: the gender the athlete picks on the team details step is
// saved there, so a team that says another gender never overwrites it (the
// server answers with a note, shown as a toast); a team typed in lands on
// the profile as a provisional CACHE team when the server made one.

const WIDE = new Set(["socialsPartial", "socialsCaptured", "performanceDone", "noData", "live", "promoApplied", "optimized", "privacy", "resume"]);
const BAND = {
  socialsPartial: (s) => ["Heads up", "Performance only · no visibility data"],
  socialsCaptured: (s, c) => (c.socialsPending || !visibilityRead(s)
    ? ["Reading your accounts", `${plural(socialCount(s), "link")} saved`]
    : ["Visibility captured", `${plural(socialCount(s), "account")} connected`]),
  performanceDone: (s) => ["Performance calculated", [s.team?.teamName, s.team?.status === "verified" ? "verified team" : "unverified team"].filter(Boolean).join(" · ")],
  noData: (s) => ["Profile created", [(s.details?.position || [])[0], s.team?.teamName, s.team?.manualTeam?.city && `${s.team.manualTeam.city}, ${s.team.manualTeam.state}`].filter(Boolean).join(" · ")],
  // 13 and 17: position, jersey and team; phones add the hometown when one
  // is saved (liveHeroSub). c.phone and c.codes come from bandFor.
  live: (s, c) => ["Your profile is live", liveHeroSub(s, { phone: c.phone, codes: c.codes })],
  promoApplied: (s) => ["Fees waived", promoHeroSub(s)],
  optimized: (s, c) => [isPremium(s) ? "Premium" : c.paymentPending ? "Confirming payment" : "Core", liveHeroSub(s, { phone: c.phone, codes: c.codes })],
};
const TOPBAR = {
  socials: { progress: { step: 2, total: 4 }, action: "Skip" },
  socialsImporting: { progress: { step: 2, total: 4 } },
  socialsFailed: { progress: { step: 2, total: 4 }, action: "Skip" },
  socialsPartial: { progress: { step: 2, total: 4 } },
  socialsCaptured: { progress: { step: 2, total: 4 } },
  teamCode: { progress: { step: 3, total: 4 } },
  teamDetails: { progress: { step: 3, total: 4 } },
  teamSearch: { progress: { step: 3, total: 4 } },
  identity: { progress: { step: 3, total: 4 } },
  teamManual: { progress: { step: 3, total: 4 } },
  earlierProfile: { progress: { step: 3, total: 4 } },
  performance: { progress: { step: 3, total: 4 } },
  seasonStats: { progress: { step: 3, total: 4 } },
  performanceDone: { progress: { step: 3, total: 4 } },
  noData: { progress: { step: 3, total: 4 } },
  photo: { progress: { step: 4, total: 4 }, action: "Skip" },
  live: { title: "" },
  plan: { title: "Unlock Optimized" },
  planFailed: { title: "Unlock Optimized" },
  promo: { title: "Have a code?", action: "Skip" },
  promoApplied: { title: "" },
  optimized: { title: "" },
  details: { title: "Add your details", action: "Skip" },
  privacy: { title: "Privacy controls", action: "Later" },
  resume: { title: "Finish your profile" },
};
// Steps from the moment the profile is live; onboarding counts as complete there.
const POST_LIVE = ["live", "plan", "planFailed", "promo", "promoApplied", "optimized", "details", "privacy"];
// Screens after the profile is live need no wizard context, so a saved one reopens as is.
const DIRECT_RESUME = new Set(["plan", "planFailed", "promo", "promoApplied", "optimized", "details", "privacy"]);
// The paywall screens. When Premium is included with the team's plan (the
// state's coverage: a coach on a paid plan) they have nothing to sell, so
// the wizard opens the Premium screen in their place.
const PAYWALL_STEPS = new Set(["plan", "planFailed", "promo"]);
const paywallOr = (step, s) => (coverageOf(s) ? "optimized" : step);
// Where a saved step reopens. Nothing sends a real card decline back here
// (Stripe shows declines on its own page), so a saved planFailed reopens on
// the plain plan; the Premium screens reopen only for a Premium account.
const reopenAt = (saved, s) => {
  if (PAYWALL_STEPS.has(saved)) return paywallOr(saved === "planFailed" ? "plan" : saved, s);
  if ((saved === "optimized" || saved === "promoApplied") && !isPremium(s)) return "plan";
  return saved;
};
const BACK = {
  socialsFailed: "socials", teamDetails: "teamCode", teamSearch: "teamDetails", identity: "teamSearch", teamManual: "teamSearch",
  performance: "teamCode", seasonStats: "performance", photo: "performance", plan: "live", planFailed: "plan", promo: "plan", details: "optimized",
};
// Details is reached from the Premium screen or, for a free athlete, from the plan.
const backOf = (step, s) => (step === "details" ? (isPremium(s) ? "optimized" : "plan") : BACK[step]);
// Phones (D-o1): Privacy (19) and Resume (21) get the close X on the left,
// which leaves like "Later" / "Not now"; Resume has no right action there.
const CLOSE = new Set(["privacy", "resume"]);

// A team link's note that the team says another gender than the profile
// keeps ("This team is listed as a boys team. Your profile keeps the gender
// you picked.").
const showFillNote = (data) => {
  const note = fillNoteOf(data);
  if (note) toast.info(note);
};

const AthleteOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const user = useSelector((s) => s.user?.user || {});
  // The codes list names the saved hometown state in the 13 and 17 hero
  // lines, and gives the CACHE sports a team typed in can play.
  const allCodes = useSelector((s) => s.codes?.allCodes);
  const sportCodes = (Array.isArray(allCodes) ? allCodes : []).filter((c) => c && c.type === "sport" && c.name);
  const [state, setState] = useState(null);
  const [step, setStep] = useState(null);
  const [ctx, setCtx] = useState({});
  const [error, setError] = useState("");
  // Keep to the account this tab opened with (auth/v2/useWizardSession):
  // another tab that logs out or logs in as someone else stops the wizard,
  // and alive() is false once it is done for. Bound before the first read.
  const { alive, end, stop, sessionLost } = useWizardSession({ bindSession, sessionChanged });
  const completedRef = useRef(false);
  // AuthFrame's phone header shows every other right action (Skip, Later,
  // Save and log out, Save and exit) as orange text with its full label.
  const phone = useIsPhone();

  // The hometown State picker's rows (codes of type state), read once the
  // details step opens. Without them the picker offers abbreviations.
  const [stateOptions, setStateOptions] = useState([]);
  useEffect(() => {
    if (step !== "details" || stateOptions.length) return undefined;
    let live = true;
    (async () => {
      try {
        const rows = await fetchStateCodes();
        if (live && Array.isArray(rows) && rows.length) setStateOptions(rows);
      } catch { /* the picker falls back to abbreviations */ }
    })();
    return () => { live = false; };
  }, [step, stateOptions.length]);

  const refresh = useCallback(async () => {
    const next = await getOnboarding();
    setState(next);
    return next;
  }, []);

  // The first read of the socials outlasts the importing step (minutes, see
  // socialScoreWatch): while the server says it is still running, the
  // wizard reads the state in the background and shows the score (the
  // bands, the live step) once it lands. The importing step polls itself.
  const socialsRunning = socialPending(state) && step !== "socialsImporting";
  useEffect(() => {
    if (!socialsRunning) return undefined;
    return watchSocialScore({
      first: state,
      read: getOnboarding,
      alive,
      onLanded: (s) => {
        setState((prev) => (prev ? { ...prev, social: s.social, metrics: s.metrics } : s));
        // The captured step stops saying "Reading your accounts".
        if (!socialPending(s)) setCtx((c) => (c.socialsPending ? { ...c, socialsPending: false } : c));
      },
    });
    // The watch reads on its own; `state` is only its starting point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialsRunning, alive]);

  const go = useCallback((next, extra) => {
    // The only place progress is saved on the way; a wizard that is gone moves nowhere.
    if (!alive()) return;
    setStep(next);
    if (extra) setCtx((c) => ({ ...c, ...extra }));
    window.scrollTo({ top: 0 });
    if (!["socialsImporting", "identity", "earlierProfile"].includes(next)) saveProgress(next).catch(() => {});
  }, [alive]);

  // Where to start: payment return, a saved step, or the first screen.
  useEffect(() => {
    (async () => {
      try {
        const s = await refresh();
        if (!alive()) return;
        const payment = params.get("payment");
        if (payment === "success") {
          setParams({}, { replace: true });
          go("optimized", { paymentPending: true });
          dispatch(fetchEntitlement());
          const { state: after } = await waitFor((x) => x.entitlement === "athlete_premium", { tries: 10, delayMs: 3000, alive });
          if (!alive() || !after) return;
          setState(after);
          setCtx((c) => ({ ...c, paymentPending: after.entitlement !== "athlete_premium" }));
          return;
        }
        // Backing out of Stripe Checkout is a cancel, not a decline.
        if (payment === "cancelled") { setParams({}, { replace: true }); go(paywallOr("plan", s), { checkoutCancelled: !coverageOf(s) }); return; }
        const saved = s.progress?.step;
        if (saved === "done" || (s.onboardingCompleted && !saved)) {
          // Sync the signed-in user first, or the route gate sends the
          // athlete straight back here. "done" is only saved once live.
          if (s.onboardingCompleted) markDoneLocally(); else await complete();
          navigate(ATHLETE_LANDING, { replace: true });
          return;
        }
        if (DIRECT_RESUME.has(saved)) setStep(reopenAt(saved, s));
        else if (saved && !["socials", "socialsImporting"].includes(saved)) setStep("resume");
        else setStep("socials");
      } catch (e) {
        if (!sessionChanged()) setError(e.message || "We could not load your onboarding.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markDoneLocally = () => {
    dispatch({ type: "FETCH_USER_BY_EMAIL", payload: { ...user, onboardingCompleted: true, options: { ...(user.options || {}), onboardingCompleted: true } } });
    const myEmail = localStorage.getItem("myEmail");
    if (myEmail) dispatch(fetchUserByEmail(myEmail));
  };

  // Onboarding counts as complete once the profile is live; the paywall,
  // details and privacy screens are extras the athlete can leave.
  const complete = async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    try { setState(await patchOnboarding({ complete: true })); } catch { /* the gate falls back to the server flag */ }
    if (!sessionChanged()) markDoneLocally();
  };

  const isLive = () => !!state?.onboardingCompleted || POST_LIVE.includes(step);

  const leave = async (to = ATHLETE_LANDING) => {
    if (sessionChanged()) { stop(); return; }
    const live = isLive();
    // Leaving before the profile is live logs out (below): from here on
    // nothing still running may move the wizard or save a step.
    if (!live) end();
    // The server may have completed onboarding already (a team code or a
    // roster claim does); the signed-in user must say so before leaving.
    if (state?.onboardingCompleted) markDoneLocally();
    else if (POST_LIVE.includes(step)) await complete();
    // The resume screen is not a step of its own: keep the step it resumes.
    if (step !== "resume") await saveProgress(step === "privacy" || step === "optimized" ? "done" : step).catch(() => {});
    if (live) { navigate(to); return; }
    // Before the profile is live every other page sends the athlete back
    // here, so leaving means logging out. The saved step reopens next time.
    toast.success("Progress saved. Log in any time to finish your profile.");
    ["token", "myEmail", "athleteEmail", "expiresAt"].forEach((k) => localStorage.removeItem(k));
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const afterTeam = async () => {
    // A team is on file: refresh the score with whatever data exists, then
    // wait for the team's stats themselves. The CacheScore proves nothing
    // here, since socials already made it non-zero before any team existed.
    if (!alive()) return;
    // Every request here has a time limit (onboardingV2Services), so the
    // step that waits on this (IdentityStep's busy state) always ends.
    try { await recomputeSelf(); } catch { /* best effort */ }
    if (!alive()) return;
    let s = null;
    try { ({ state: s } = await waitFor((x) => !!x.performance?.source, { tries: 6, delayMs: 2500, alive, timeoutMs: SCORE_REFRESH_TIMEOUT_MS })); } catch { /* decide on what is known */ }
    if (!alive()) return;
    if (s) setState(s);
    if (s?.performance?.source) { go("performanceDone"); return; }
    if (s?.team?.playerId) {
      // A roster spot is claimed; its stats arrive with the background refresh.
      toast.info("Your team is linked. Your game stats show on your profile once they sync.");
      go("photo");
      return;
    }
    go("performance");
  };

  // Once a team is on file: when an earlier profile (a pilot record) looks
  // like this athlete's, ask about it before moving on with `next`.
  const thenEarlier = async (next) => {
    let found = [];
    try { found = await fetchEarlierProfiles(); } catch { /* nothing to ask about */ }
    if (!alive()) return;
    if (Array.isArray(found) && found.length) { go("earlierProfile", { earlier: found, afterEarlier: next }); return; }
    await next();
  };

  // The gender picked on the team details step is the athlete's own answer:
  // saved before any team is picked, so a team that says otherwise fills
  // nothing over it (best effort; it is sent again after the pick).
  const rememberGender = (gender) => {
    if (gender === "M" || gender === "F") Promise.resolve().then(() => patchOnboarding({ gender })).catch(() => {});
  };

  // The gender picked again on the manual step, for a team picked from its
  // matches (they are that gender's): saved as the details step's is, and
  // the roster step files the team under it.
  const manualGender = (gender) => {
    if (gender !== "M" && gender !== "F") return;
    if (gender !== ctx.draft?.gender) rememberGender(gender);
    setCtx((c) => ({ ...c, draft: { ...(c.draft || {}), gender } }));
  };

  // A team typed in (the team request): the server put it on the profile
  // as a provisional CACHE team (`answer.team`), else it is kept as the
  // manual team the profile files (a sport CACHE keeps no teams for has no
  // team to wait for: the server files the details only). Either answer can
  // carry the gender note. Then on as the manual path goes.
  const afterManualTeam = async (manual, answer) => {
    showFillNote(answer);
    if (answer?.team) {
      await patchOnboarding({ gender: manual.gender || "" }).catch(() => {});
      await refresh();
    } else {
      setState(await patchOnboarding({ manualTeam: manual, gender: manual.gender || "" }));
    }
    await thenEarlier(async () => go((state.social?.score || 0) > 0 ? "performance" : "noData"));
  };

  // A team code typed on the promo screen joined the team (PlanSteps
  // JoinWithCode). A team plan or a CACHE team code can include Premium
  // (10/19 notes, D10), so the wizard reads its state and the entitlement
  // again and skips the paywall when the team now covers the athlete.
  const joinedFromPromo = async () => {
    let s = state;
    try { s = await refresh(); } catch { /* the plan step, as before */ }
    if (!alive()) return;
    dispatch(fetchEntitlement());
    go(paywallOr("plan", s));
  };

  const handlers = {
    saveSocials: async (urls) => {
      const before = state.social?.computedAt || null;
      // The same list keeps sports links (MaxPreps, Hudl); send them back too.
      const others = (state.socialMedia || []).filter((u) => !isSocialLink(u));
      const saved = await patchOnboarding({ socialMedia: [...urls, ...others], recompute: true });
      if (!alive()) return;
      setState(saved);
      go("socialsImporting");
      const read = visibilityRead; // followers or a score came back
      // A run that finished after this save carries a new computedAt; a
      // status left over from an earlier run must not end the wait.
      const fresh = (x) => !!x?.social?.computedAt && x.social.computedAt !== before;
      let polled = { state: null, ok: false };
      try { polled = await waitFor((x) => read(x) || fresh(x), { tries: 10, delayMs: 3000, alive }); } catch { /* treated as still reading */ }
      // Logged out or gone while the poll ran: no toast, no step, no save.
      if (!alive()) return;
      const { state: s, ok } = polled;
      if (s) setState(s);
      if (read(s)) go("socialsCaptured", { socialsPending: false });
      else if (ok) go("socialsFailed");
      else { toast.info("Still reading your accounts. Your score updates on your profile when it lands."); go("socialsCaptured", { socialsPending: true }); }
    },
    skipSocials: () => go("socialsPartial"),
    uploadPhoto: async (file) => {
      const small = await compressImage(file);
      const out = await uploadAvatar(small, state.email);
      await refresh();
      return out?.avatar;
    },
    subscribe: async (interval) => {
      const out = await createCheckout(interval);
      if (out?.checkoutUrl) { await saveProgress("plan").catch(() => {}); window.location.assign(out.checkoutUrl); return; }
      if (out?.upgraded) { await refresh(); go("optimized"); return; }
      throw new Error("Checkout did not start. Try again.");
    },
    applyPromo: async (code) => {
      const out = await redeemPromo(code);
      await dispatch(fetchEntitlement());
      await refresh();
      go("promoApplied", { promo: out });
    },
  };

  if (sessionLost) {
    return (
      <AuthFrame title="Onboarding">
        <TitleBlock eyebrow="Session changed" title="This page is out of date" sub="You signed out or signed in to a different account in another tab. Nothing more was saved from this page." />
        <Actions><PrimaryBtn onClick={() => window.location.reload()}>Reload</PrimaryBtn></Actions>
      </AuthFrame>
    );
  }
  if (error) return <AuthFrame title="Onboarding"><div className="pt-10 text-center bm-body-m text-bm-critical">{error}</div></AuthFrame>;
  if (!state || !step) return <AuthFrame title="Setting up"><Loading label="Loading your profile..." /></AuthFrame>;

  const bar = TOPBAR[step] || {};
  const actionFor = () => {
    if (bar.action === "Skip" && step === "socials") return { label: "Skip", onClick: handlers.skipSocials };
    if (bar.action === "Skip" && step === "socialsFailed") return { label: "Skip", onClick: () => go("socialsPartial") };
    if (bar.action === "Skip" && step === "photo") return { label: "Skip", onClick: () => { complete(); go("live"); } };
    if (bar.action === "Skip" && step === "promo") return { label: "Skip", onClick: () => go("plan") };
    if (bar.action === "Skip" && step === "details") return { label: "Skip", onClick: () => go("privacy") };
    if (bar.action === "Later" && step === "privacy") return { label: "Later", onClick: () => leave() };
    return { label: isLive() ? "Save and exit" : "Save and log out", onClick: () => leave() };
  };
  const bandFor = () => {
    if (!WIDE.has(step) || !BAND[step]) return null;
    const [badge, sub] = BAND[step](state, { ...ctx, phone, codes: allCodes || [] });
    return <HeroBand initials={initialsOf(state.firstName, state.lastName)} avatar={state.avatar} badge={badge} name={`${state.firstName} ${state.lastName}`.trim()} sub={sub} />;
  };

  const body = () => {
    switch (step) {
      case "resume": return <ResumeStep state={state} savedStep={state.progress?.step || ""} onContinue={(next) => go({ socials: "socials", team: "teamCode", stats: ["seasonStats", "noData"].includes(state.progress?.step) ? state.progress.step : "performance", photo: "photo", live: "live" }[next])} onNotNow={() => leave()} notNowLabel={isLive() ? "Not now" : "Not now, log out"} />;
      case "socials": return <SocialLinksStep state={state} onSave={handlers.saveSocials} onSkip={handlers.skipSocials} />;
      case "socialsImporting": return <SocialImportingStep platformLabel={(state.socialMedia || []).some((u) => /instagram/i.test(u)) ? "your Instagram" : "your socials"} />;
      case "socialsFailed": return <SocialFailedStep state={state} onRetry={() => go("socials")} onContinue={() => go("socialsPartial")} />;
      case "socialsPartial": return <SocialPartialStep state={state} onConnect={() => go("socials")} onContinue={() => go("teamCode")} />;
      case "socialsCaptured": return <SocialCapturedStep state={state} pending={!!ctx.socialsPending} onNext={() => go("teamCode")} />;
      case "teamCode": return <TeamCodeStep state={state} onLinked={async (data) => { await refresh(); if (!alive()) return; toast.success("Team verified."); showFillNote(data); await thenEarlier(afterTeam); }} onNoCode={() => go("teamDetails")} />;
      // The search step opens with what the details step found and whether
      // the search was limited (the team feed down: dataStatus).
      case "teamDetails": return <TeamDetailsStep state={state} onResults={(rows, draft, dataStatus) => { rememberGender(draft.gender); go("teamSearch", { rows, draft, dataStatus: dataStatus || null }); }} onNoResults={(draft, dataStatus) => { rememberGender(draft.gender); go("teamSearch", { rows: [], draft, dataStatus: dataStatus || null }); }} />;
      case "teamSearch": return <TeamSearchStep initialQuery={ctx.draft?.name || ""} initialRows={ctx.rows ?? null} initialStatus={ctx.dataStatus || null} gender={ctx.draft?.gender} onPick={(t) => go("identity", { team: t })} onManual={(q) => go("teamManual", { draft: { ...(ctx.draft || {}), cerebroProgramId: "", name: q || ctx.draft?.name || "" } })} />;
      // A program with no roster binds like any team (a provisional CACHE
      // team, made by the server): no detour through the manual path.
      // The roster step files the team under the gender the athlete just
      // picked (their own answer), not only the one the profile had.
      case "identity": return <IdentityStep state={ctx.draft?.gender ? { ...state, details: { ...(state.details || {}), gender: ctx.draft.gender } } : state} team={ctx.team} onDone={async (out) => { showFillNote(out); await patchOnboarding({ gender: ctx.draft?.gender || state.details?.gender || "" }).catch(() => {}); await refresh(); await thenEarlier(afterTeam); }} onNotMe={() => go("teamSearch")} />;
      case "teamManual": return <TeamManualStep draft={ctx.draft} sports={sportCodes} onGender={manualGender} onPick={(t) => go("identity", { team: t })} onSaved={afterManualTeam} />;
      case "earlierProfile": return <EarlierProfileStep candidates={ctx.earlier || []} onClaim={claimEarlierProfile} onContinue={async () => { const next = ctx.afterEarlier; if (typeof next === "function") await next(); else await afterTeam(); }} />;
      // The profile link is kept with the athlete's other links (the edit hub
      // lists it as a sports link). Nothing reads stats from it, and the
      // wizard only counts social network links as visibility.
      case "performance": return <PerformanceChoiceStep state={state} onImport={async (link) => { const saved = await patchOnboarding({ socialMedia: [...(state.socialMedia || []), link] }); if (!alive()) return; setState(saved); toast.info("We saved your profile link. Enter your season numbers any time to add the performance half."); go("photo"); }} onManual={() => go("seasonStats")} onSkip={() => go("photo")} />;
      case "seasonStats": return <SeasonStatsStep onSave={async (stats) => {
        const saved = await patchOnboarding({ seasonStats: stats, recompute: true });
        if (!alive()) return;
        setState(saved);
        const { state: s } = await waitFor((x) => (x.metrics?.cacheScore || 0) > 0 || (x.metrics?.performance || 0) > 0, { tries: 5, delayMs: 2500, alive });
        if (!alive()) return;
        if (s) setState(s);
        go("performanceDone");
      }} />;
      case "performanceDone": return <PerformanceDoneStep state={state} onNext={() => go("photo")} />;
      case "noData": return <NoDataStep state={state} onStats={() => go("performance")} onSocial={() => go("socials")} onNext={() => go("photo")} />;
      case "photo": return <PhotoStep state={state} onUpload={handlers.uploadPhoto} onSkip={async () => { await complete(); go("live"); }} />;
      case "live": return <ProfileLiveStep state={state} onUnlock={() => go(paywallOr("plan", state))} onProfile={() => leave("/athlete/profile")} />;
      case "plan": return <PlanStep cancelled={!!ctx.checkoutCancelled} onSubscribe={handlers.subscribe} onCode={() => go("promo", { checkoutCancelled: false })} onKeepCore={() => go("details", { checkoutCancelled: false })} />;
      case "planFailed": return <PlanStep declined onSubscribe={handlers.subscribe} onCode={() => go("promo")} onKeepCore={() => go("details")} />;
      case "promo": return <PromoStep onApply={handlers.applyPromo} onSkip={() => go("plan")} onJoined={joinedFromPromo} />;
      case "promoApplied": return <PromoAppliedStep state={state} promo={ctx.promo || { code: state.promoCode }} onNext={() => go("optimized")} />;
      case "optimized": return <OptimizedStep state={state} pending={!!ctx.paymentPending} onProfile={() => go("details")} onCircle={() => leave("/athlete/circle")} />;
      // The step reads the state again when it opens (onReload): the
      // profile may have been pre-filled from the team since, and only the
      // fields the athlete changes are sent.
      case "details": return <DetailsStep state={state} stateOptions={stateOptions} onReload={refresh} onSave={async (details) => { setState(await patchOnboarding(details)); go("privacy"); }} onSkip={() => go("privacy")} />;
      case "privacy": return <PrivacyStep state={state} onSave={async (prefs) => { await patchOnboarding({ privacyPrefs: prefs }); await complete(); await saveProgress("done").catch(() => {}); toast.success("Your profile is set."); navigate(ATHLETE_LANDING); }} onCircle={() => leave("/athlete/circle")} />;
      default: return null;
    }
  };

  return (
    <AuthFrame
      title={bar.title}
      progress={bar.progress}
      onBack={backOf(step, state) ? () => go(backOf(step, state)) : undefined}
      onClose={CLOSE.has(step) ? () => leave() : undefined}
      action={phone && step === "resume" ? undefined : actionFor()}
      wide={WIDE.has(step)}
      band={bandFor()}
    >
      {body()}
    </AuthFrame>
  );
};

export default AthleteOnboarding;
